import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useUnit } from "effector-react";
import { useViewportSize } from "./ViewportContext";
import { $appConfig, updateCameraConfig } from "../state/appConfig";
import { $safeAreaInsets, type SafeAreaInsets, ZERO_SAFE_AREA } from "../state/safeArea";

export type ChatPanelMode =
  | "hidden"
  | "half-screen"
  | "full-screen";

// Layout constants
export const APP_INSET = 8;
export const CARD_GAP = 8;
export const HUD_INDENT = 16;
export const CHAT_INPUT_HEIGHT = 48;
export const PANEL_SUGGESTIONS_GAP = 16;
export const SUGGESTIONS_HEIGHT = 36;
export const POI_CHIPS_HEIGHT = 32;
export const CANVAS_PANEL_GAP = 24; // matches --canvas-panel-gap in spacing.css
export const HALF_SCREEN_SCENE_HEIGHT = 220;
/**
 * Desktop sidebar width (matches Figma design 41ukbWLMovnvWQe57GKsJE).
 * Mirrored into the `--chat-sidebar-width` CSS-var by `AssistantChatSidebar`
 * at mount; mobile leaves the var at its `0px` default.
 */
export const CHAT_SIDEBAR_WIDTH = 420;
/**
 * Gap between the desktop chat sidebar and the canvas (separate from the
 * mobile `CANVAS_PANEL_GAP` since the two layouts use independent edge
 * spacing). Mirrored into `--canvas-side-gap` by `AssistantChatSidebar`.
 */
export const CANVAS_SIDEBAR_GAP = 8;
/**
 * Slide-in duration for the desktop chat sidebar. Matches the mobile
 * panel's `hidden → half-screen` transition (600 ms) so the canvas
 * reveal animation lands in the same time window the user is used to.
 */
export const SIDEBAR_TRANSITION_DURATION = 600;
/** Delay (ms) after scene reveal before showing HUD elements (chat, markers, menu). */
export const HUD_REVEAL_DELAY = 200;
/**
 * Default camera view offset for "hidden" chat panel mode.
 * Used to initialize camera offset before React components mount.
 * Assumes safe-area-bottom = 0 (pre-mount fallback).
 */
export const DEFAULT_HIDDEN_MODE_OFFSET = CARD_GAP + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT;

export const getCurtainHeight = (
  mode: ChatPanelMode,
  viewportHeight: number,
  shift = 0,
  safeArea: SafeAreaInsets = ZERO_SAFE_AREA,
  extraFooterHeight = 0,
): number => {
  const baseHeight = (() => {
    switch (mode) {
      case "hidden":
        return CARD_GAP + safeArea.bottom + CHAT_INPUT_HEIGHT + 2 * HUD_INDENT + extraFooterHeight;
      case "half-screen":
        return CARD_GAP + viewportHeight - safeArea.top - 2 * CARD_GAP - CANVAS_PANEL_GAP - HALF_SCREEN_SCENE_HEIGHT;
      case "full-screen":
        return CARD_GAP + 0.8 * (viewportHeight - safeArea.top - 2 * CARD_GAP);
    }
  })();

  return Math.max(0, baseHeight - shift);
};

export const getCurtainOffset = (
  mode: ChatPanelMode,
  viewportHeight: number,
  shift = 0,
  safeArea: SafeAreaInsets = ZERO_SAFE_AREA,
  _bottomInset = 0,
  extraFooterHeight = 0,
): number => {
  return getCurtainHeight(mode, viewportHeight, shift, safeArea, extraFooterHeight);
};

type LayoutOffsetPatch = Partial<{
  top: number;
  right: number;
  bottom: number;
  left: number;
}>;

type LayoutOffset = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

interface CameraViewOffsetContextValue {
  /**
   * Mobile chat-panel curtain offset (writes only `bottom`; resets
   * `top` to `safeArea.top` and zeroes `right`/`left`). Kept as a
   * narrow alias for the panel-mode use case.
   */
  applyCurtainOffset: (
    bottomOffset: number,
    animate: boolean,
    durationMs?: number
  ) => void;
  /**
   * General-purpose offset writer. Updates only the sides present in
   * `patch`; other sides retain their previously-applied values. Use
   * this for the desktop sidebar (writes `right`/`left`) and for any
   * future overlay that needs to reserve a slice of the canvas.
   */
  applyLayoutOffset: (
    patch: LayoutOffsetPatch,
    animate: boolean,
    durationMs?: number
  ) => void;
  /** Calculate curtain offset for given mode and shift */
  getCurtainOffset: (mode: ChatPanelMode, shift?: number) => number;
  /** Current viewport dimensions */
  viewport: { width: number; height: number };
  /** Whether initial offset has been applied */
  isInitialized: boolean;
}

const CameraViewOffsetContext = createContext<CameraViewOffsetContextValue | null>(null);

interface CameraViewOffsetProviderProps {
  children: ReactNode;
  /**
   * When true, applies default offset immediately (without animation).
   * Typically set to true when scene preview is ready.
   */
  enabled?: boolean;
  /**
   * Changes when the active scene switches.
   * Resets `hasAppliedInitialRef` so the default offset is re-applied for the new scene.
   */
  sceneKey?: string | null;
}

export function CameraViewOffsetProvider({
  children,
  enabled = false,
  sceneKey,
}: CameraViewOffsetProviderProps) {
  const viewport = useViewportSize();
  const safeArea = useUnit($safeAreaInsets);
  const { appConfig, pushCameraConfig } = useUnit({
    appConfig: $appConfig,
    pushCameraConfig: updateCameraConfig,
  });

  // Initialize from current store state to sync with createScene.ts
  const initialOffset: LayoutOffset = appConfig.camera.viewOffset.enabled
    ? {
        top: appConfig.camera.viewOffset.top,
        right: appConfig.camera.viewOffset.right,
        bottom: appConfig.camera.viewOffset.bottom,
        left: appConfig.camera.viewOffset.left,
      }
    : { top: 0, right: 0, bottom: 0, left: 0 };
  const offsetRef = useRef<LayoutOffset>(initialOffset);
  const hasAppliedInitialRef = useRef(false);

  const applyLayoutOffset = useCallback(
    (patch: LayoutOffsetPatch, animate: boolean, durationMs?: number) => {
      const cur = offsetRef.current;
      // Clamp bottom against half-screen curtain height (matches the
      // existing mobile-panel ceiling — without this the camera could
      // be pushed out of view in degenerate cases). Other sides only
      // get non-negative clamping; the canvas size already bounds them.
      const maxBottom = Math.round(
        getCurtainHeight("half-screen", viewport.height, 0, safeArea)
      );
      const clamp = (value: number, max?: number) => {
        const rounded = Math.round(value);
        const floor = Math.max(rounded, 0);
        return max != null ? Math.min(floor, max) : floor;
      };

      const next: LayoutOffset = {
        top: patch.top != null ? clamp(patch.top) : cur.top,
        right: patch.right != null ? clamp(patch.right) : cur.right,
        bottom: patch.bottom != null ? clamp(patch.bottom, maxBottom) : cur.bottom,
        left: patch.left != null ? clamp(patch.left) : cur.left,
      };

      // Skip no-op writes when not animating (prevents duplicate calls
      // from racing useEffect deps + re-rendering subscribers).
      if (
        !animate
        && cur.top === next.top
        && cur.right === next.right
        && cur.bottom === next.bottom
        && cur.left === next.left
      ) {
        return;
      }

      offsetRef.current = next;

      pushCameraConfig({
        viewOffset: {
          enabled: true,
          top: next.top,
          right: next.right,
          bottom: next.bottom,
          left: next.left,
          animate,
          animationDurationMs: durationMs,
        },
      });
    },
    [pushCameraConfig, safeArea, viewport.height]
  );

  // Backward-compatible curtain offset writer. The mobile chat panel
  // semantics: reserves `bottom` for the curtain, resets `top` to
  // `safeArea.top`, and zeroes horizontal sides — exactly what the
  // panel needs on every mode/drag change. Equivalent to
  // `applyLayoutOffset({top, right: 0, bottom, left: 0}, ...)`.
  const applyCurtainOffset = useCallback(
    (bottomOffset: number, animate: boolean, durationMs?: number) => {
      applyLayoutOffset(
        {
          top: safeArea.top,
          right: 0,
          bottom: bottomOffset,
          left: 0,
        },
        animate,
        durationMs,
      );
    },
    [applyLayoutOffset, safeArea.top]
  );

  const getCurtainOffsetForMode = useCallback(
    (mode: ChatPanelMode, shift = 0) =>
      getCurtainOffset(
        mode,
        viewport.height,
        shift,
        safeArea,
      ),
    [safeArea, viewport.height]
  );

  // Reset initial offset flag when scene changes so we re-apply for the new scene
  useEffect(() => {
    hasAppliedInitialRef.current = false;
  }, [sceneKey]);

  // Apply default offset when enabled becomes true (e.g., at preview stage)
  useEffect(() => {
    if (enabled && !hasAppliedInitialRef.current) {
      hasAppliedInitialRef.current = true;
      applyCurtainOffset(
        getCurtainOffset(
          "hidden",
          viewport.height,
          0,
          safeArea,
        ),
        false
      );
    }
  }, [enabled, applyCurtainOffset, safeArea, viewport.height]);

  const value = useMemo<CameraViewOffsetContextValue>(
    () => ({
      applyCurtainOffset,
      applyLayoutOffset,
      getCurtainOffset: getCurtainOffsetForMode,
      viewport,
      isInitialized: hasAppliedInitialRef.current,
    }),
    [applyCurtainOffset, applyLayoutOffset, getCurtainOffsetForMode, viewport]
  );

  return (
    <CameraViewOffsetContext.Provider value={value}>
      {children}
    </CameraViewOffsetContext.Provider>
  );
}

export function useCameraViewOffset(): CameraViewOffsetContextValue {
  const context = useContext(CameraViewOffsetContext);
  if (!context) {
    throw new Error(
      "useCameraViewOffset must be used within CameraViewOffsetProvider"
    );
  }
  return context;
}
